import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import IconRoundBarkBgWhite from '@/assets/logos/icon-round-bark-bg-svg/icon-round-bark-bg-white.svg';
import { SunlightPatternBackground } from '@/features/onboarding-v2/components/SunlightPatternBackground';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { Button } from '@heidi/design-system/button';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FormattedMessage, useIntl } from 'react-intl';

import { ChoiceCard } from './choice-card';

interface BookDemoConfirmationStepProps {
  email: string;
}

export function BookDemoConfirmationStep({ email }: BookDemoConfirmationStepProps) {
  const router = useRouter();
  const intl = useIntl();
  const { isAuthenticated } = useIsAuthenticated();

  const handleContinueToSignup = () => {
    if (isAuthenticated) {
      void router.push('/scribe/new-user');
    } else {
      void router.push({ pathname: '/', query: { loginHint: email } });
    }
  };

  const handleReturnToWebsite = () => {
    window.location.href = 'https://www.heidihealth.com';
  };

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      {/* Header */}
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt="Heidi"
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage id="DR+hgyjshH" defaultMessage="Demo booked" />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              id="gBhwpifmyd"
              defaultMessage="Looking forward to speaking with you soon!"
            />
          </TypographyP2>
        </div>
      </div>

      {/* Actions card */}
      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        <ChoiceCard
          title={intl.formatMessage({
            id: 'cPh9OCFhsM',
            defaultMessage: 'Sign up free',
          })}
          description={intl.formatMessage({
            id: 'TpStGaZtU0',
            defaultMessage: 'Get started in seconds',
          })}
          data-testid="demo-continue-signup"
          icon={
            <div className="relative flex size-full items-center justify-center">
              <SunlightPatternBackground />
              <div className="relative z-10 rounded-full border border-bark-900/20 p-1.5">
                <div className="rounded-full border border-bark-900/30 p-1">
                  <Image src={IconRoundBarkBgWhite} alt="" width={44} height={44} />
                </div>
              </div>
            </div>
          }
          onClick={handleContinueToSignup}
        />
        <Button
          size="lg"
          variant="link"
          onClick={handleReturnToWebsite}
          className="text-text-secondary"
          data-testid="demo-return-website"
        >
          <FormattedMessage id="pyJuhkhg9l" defaultMessage="Or return to Heidi" />
        </Button>
      </div>
    </div>
  );
}
